// The TECHNOHULL Vessel Entry & Work Control Form (ΕΝΤΥΠΟ ΕΙΣΟΔΟΥ ΣΚΑΦΟΥΣ & ΕΛΕΓΧΟΥ ΕΡΓΑΣΙΩΝ), the
// customer's own one-page form, filled in from a Service vessel's Entry form button. Its record is a boat
// yard record (boatDocs.ts): the vessel's name, model, SAP, Arrival date (entryDate) and Handover Date
// (exitDate) are the record's own columns, so the one-open-visit rule, the vessel link and the Documents
// list keep working. Everything else on the form is this "control" part, kept as JSON in the record's
// control column (migration 0053) and handed back as it was saved.
// Section 2 is the customer's revised arrival inspection: A. arrival visual condition and B. basic functional
// check (each row OK / DEF / N/T / N/A with a comment), C. who inspected it, and whether photos were taken.
// Blanks are '' (a text never filled, a date not set, a row not checked); a box is ticked or not.
import { z } from 'zod';
/** section 2's A. ARRIVAL VISUAL CONDITION rows, in the form's order. */
export const VISUAL_KEYS = ['hull', 'tubes', 'deck', 'windshield', 'seating', 'engines', 'keys'];
/** section 2's B. BASIC FUNCTIONAL CHECK rows, in the form's order. */
export const FUNCTIONAL_KEYS = ['chartplotter', 'gps', 'navLights', 'deckLights', 'bilgePumps', 'freshWater'];
/** a check's result: OK (checked), DEF (defect: damage or fault), N/T (not tested), N/A (not applicable);
 *  '' while it isn't checked. */
export const CHECK_RESULTS = ['ok', 'def', 'nt', 'na'];
/** what section C's Photos says: Yes / No, '' while neither is ticked. */
export const PHOTOS_ANSWERS = ['yes', 'no'];
/** how long each written part may be: a line on the paper form, or the few lines of its free-text box
 *  (the printout shrinks a long text to keep the form on one A4 page). */
export const VESSEL_ENTRY_LIMITS = {
    comment: 120,
    name: 120,
    longText: 1000,
};
/** a real calendar day, 'YYYY-MM-DD' (not 2026-02-30). */
function isRealDay(s) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m)
        return false;
    const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}
const day = (what) => z.string().refine((s) => s === '' || isRealDay(s), { message: `${what}: pick a date` }).default('');
const text = (max, what) => z.string().max(max, `${what}: keep it under ${max} characters`).default('');
const box = z.boolean().default(false);
const CheckSchema = z
    .object({
    result: z.enum(['', ...CHECK_RESULTS], { errorMap: () => ({ message: 'A check is OK, DEF, N/T or N/A' }) }).default(''),
    comment: text(VESSEL_ENTRY_LIMITS.comment, 'Comment'),
})
    .strict();
const check = CheckSchema.default({});
const VisualSchema = z
    .object({ hull: check, tubes: check, deck: check, windshield: check, seating: check, engines: check, keys: check })
    .strict();
const FunctionalSchema = z
    .object({ chartplotter: check, gps: check, navLights: check, deckLights: check, bilgePumps: check, freshWater: check })
    .strict();
export const VesselEntryControlSchema = z
    .object({
    // 2. Arrival & initial condition check (its Arrival date is the record's entryDate)
    visual: VisualSchema.default({}),
    functional: FunctionalSchema.default({}),
    inspectedBy: text(VESSEL_ENTRY_LIMITS.name, 'Inspected by'),
    inspectedOn: day('Date'),
    technician: text(VESSEL_ENTRY_LIMITS.name, 'Technician'),
    photos: z.enum(['', ...PHOTOS_ANSWERS], { errorMap: () => ({ message: 'Photos is Yes or No' }) }).default(''),
    // 3. Administration & system control
    rolodexEntry: box,
    jobFileOpened: box,
    registeredOn: day('Date Registered'),
    registeredBy: text(VESSEL_ENTRY_LIMITS.name, 'Registered by'),
    // 4. Scope of work control
    workRequestReceived: box,
    scopeIssued: box,
    criticalItems: text(VESSEL_ENTRY_LIMITS.longText, 'Critical pending items'),
    // 5. Schedule, execution, completion & handover (Handover Date is the record's exitDate)
    worksCommenced: day('Works Commenced'),
    estimatedCompletion: day('Estimated Completion'),
})
    .strict();
/** a control part with nothing filled in: every text and date blank, every box and check unticked. */
export const emptyVesselEntryControl = () => VesselEntryControlSchema.parse({});
//# sourceMappingURL=vesselEntry.js.map